/**
 * `(GameId, move)` → `SoundKind` のマッピング。
 *
 * Poker は action 種別ごとに音を変える (= bet/raise はチップ、 fold はカードスライド、
 * deal/reveal はカード)。 他ゲームは v1 では move 内容を見ない (= 単一音)。
 *
 * 取り音 (capture) は move 内容で判定する必要があるが、 v1 では未配線 (= デフォルト
 * 着手音で統一)。 v2 で実装。
 */

import type { GameId } from '../wasm';
import type { SoundKind } from './audio';

/** ゲームごとの「通常着手音」 (= move 不問のデフォルト)。 */
function defaultKindFor(gameId: GameId): SoundKind {
  switch (gameId) {
    case 'Shogi':
    case 'Chess':
    case 'AnimalShogi':
    case 'Xiangqi':
    case 'Janggi':
    case 'Draughts':
    case 'TwixT':
      return 'wood';
    case 'Abalone':
    case 'Gomoku':
    case 'Kyoen':
    case 'Renju':
    case 'Reversi':
    case 'Connect6':
    case 'Jungo':
    case 'Kalah':
      return 'stone';
    case 'Backgammon':
      return 'wood';
    case 'BridgeNT':
    case 'HeadsUpPoker':
      return 'card';
    default:
      return 'wood';
  }
}

/** Poker move (= JSON で string か object) を SoundKind に分類。
 *  unit variant (Fold/Check/Call/AllIn/NextHand) は string、
 *  struct variant (Bet/Raise/ProvideMyHole/ProvideBoardCards) は object。
 *  PokerMove は `#[serde(rename_all = "snake_case")]` で snake_case 化されている。 */
function pokerKind(mv: unknown): SoundKind {
  if (typeof mv === 'string') {
    switch (mv) {
      case 'fold':
        return 'fold';
      case 'check':
      case 'call':
      case 'all_in':
        return 'chip';
      case 'next_hand':
        return 'card';
      default:
        return 'card';
    }
  }
  if (typeof mv === 'object' && mv !== null) {
    if ('bet' in mv || 'raise' in mv) return 'chip';
    if ('provide_my_hole' in mv || 'provide_board_cards' in mv) return 'card';
  }
  return 'card';
}

/** `(gameId, mv)` の組から SoundKind を決定。 mv が無い (= 初手前など) は defaultKindFor。 */
export function kindForMove(gameId: GameId, mv: unknown): SoundKind {
  if (gameId === 'HeadsUpPoker' && mv !== undefined) return pokerKind(mv);
  return defaultKindFor(gameId);
}
